/**
 * CombatVfx — world-space combat effects for battles: muzzle flashes,
 * bolts (tracers) from shooter to target, impact bursts, explosions, heal
 * puffs. A port of SpaceModule's fxImpact anatomy (hot core flash → thrown
 * sparks → smoke) at human scale. Bounded: at most FX_CAP live sprites; extra
 * calls are dropped, never queued.
 *
 * EXPLOSIONS ARE STAGED (fire realism R5, 2026-09-25 — lib/vfx/explosion_fx.ts):
 * a broker FLASH (25 m, 150 ms) → the fire shader's FIREBALL that cools into
 * its own smoke → a PLANAR shockwave ring on the ground + a dust skirt (+ a
 * heat-haze band at high+) — the camera-facing ring sprite is retired → 6–14
 * DEBRIS chunks, hot and cooling, trailing embers → a smoke column that
 * lingers 10–20 s → the 'blast' scorch → an AFTER-FIRE burning 20–60 s where a
 * wreck-sized blast (scale ≥ 2.5: a crash, a cruiser) went off. The report's
 * delay belongs to the caller's sound: `explosionSoundDelayS(p)` (distance /
 * 343 m/s) — the callers (battles, war air, the vehicle cannon) play it.
 * Without a fire system (never in the game — the module is always up) the old
 * sprite explosion stands in.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { glowTexture, ringTexture, spriteOf, disposeSprite, isShared } from './fx_textures.js';
import { disposeLater } from '../../lib/gpu_dispose.js';
import { fireSystem } from '../../lib/vfx/fire_system.js';
import { soundDelayS } from '../../lib/vfx/fire_rules.js';
import { blastFlashCandela, BLAST_FLASH_MS, EMITTER_NITS, EMITTER_DISPLAY_MAX } from '../../lib/vfx/fire_rules.js'; // surfaces (2026-09-27): §4.3
import { shockRingGeometry, shockRingMaterial, dustSkirtGeometry, dustSkirtMaterial, createDebrisPool, throwDebris, stepDebris, blastHazeMaterial, type DebrisPool } from '../../lib/vfx/explosion_fx.js';
import { groundYIn } from '../../lib/grounding.js';
import { atlas } from '../../state.js';

let blastSeq = 0;
/** Seconds until an explosion at `p` is HEARD at the player (the speed of sound) — for the caller's `sfxAt(…, { delayS })`. */
export function explosionSoundDelayS(p: { x: number; y: number; z: number }): number {
  return soundDelayS(Math.hypot(p.x - atlas.localX, p.y - atlas.localY, p.z - atlas.localZ));
}
function groundAt(x: number, z: number, fallback: number): number {
  try { return atlas.conn ? groundYIn(atlas.conn, atlas.currentWorldId, x, z) : fallback; } catch { return fallback; }
}
interface Wave { m: THREE.Mesh; kind: 'ring' | 'skirt' | 'haze'; age: number; life: number; r0: number; r1: number; h: number; u: THREE.Vector4 }

const FX_CAP = 160;
const UP = new THREE.Vector3(0, 1, 0);

interface Fx { s: THREE.Sprite | THREE.Mesh; vel: THREE.Vector3 | null; ttl: number; life: number; grow: number; from: number; gravity: number; len?: number }

/** One unit cylinder for every bolt (scaled per shot) and one material per tint — never a fresh pipeline per shot. */
const ROD_GEO = new THREE.CylinderGeometry(1, 1, 1, 5, 1, true);
const rodMaterials = new Map<number, THREE.MeshBasicMaterial>();
function rodMaterial(tint: number): THREE.MeshBasicMaterial {
  let m = rodMaterials.get(tint);
  if (!m) { m = new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false }); m.userData.shared = true; markMrtOverlay(m); rodMaterials.set(tint, m); }
  return m;
}

export class CombatVfx {
  readonly root = new THREE.Group();
  private live: Fx[] = [];
  readonly stats = { flashes: 0, bolts: 0, impacts: 0, explosions: 0, heals: 0, dropped: 0 };
  private readonly _d = new THREE.Vector3();
  private readonly _q = new THREE.Quaternion();
  /** The planar shockwave rings and dust skirts in flight (shared geometry + one material each). */
  private waves: Wave[] = [];
  private readonly wavePool: THREE.Mesh[] = [];
  private debris: DebrisPool | null = null;

  private push(fx: Fx): boolean {
    if (this.live.length >= FX_CAP) { this.stats.dropped++; disposeAny(fx.s); return false; }
    this.live.push(fx);
    return true;
  }

  /** The shot leaves the gun: a bright core + a puff. */
  muzzle(p: THREE.Vector3, tint = 0xffe6a8): void {
    this.stats.flashes++;
    const flash = spriteOf(glowTexture(), tint, 0.55);
    flash.position.copy(p); this.root.add(flash);
    this.push({ s: flash, vel: null, ttl: 0.07, life: 0.07, grow: 0.9, from: 0.55, gravity: 0 });
    const puff = spriteOf(glowTexture(), 0x8f8a80, 0.25, false, 0.35);
    puff.position.copy(p); this.root.add(puff);
    this.push({ s: puff, vel: new THREE.Vector3(0, 0.6, 0), ttl: 0.5, life: 0.5, grow: 0.9, from: 0.25, gravity: 0 });
  }

  /** A bolt streaks shooter → target over ~120 ms: a thin glowing rod + a moving head. */
  bolt(from: THREE.Vector3, to: THREE.Vector3, tint = 0x9fe8ff): void {
    this.stats.bolts++;
    const len = from.distanceTo(to);
    if (len < 0.2) return;
    const rod = new THREE.Mesh(ROD_GEO, rodMaterial(tint));
    rod.scale.set(0.025, len, 0.025);
    rod.position.copy(from).add(to).multiplyScalar(0.5);
    this._d.copy(to).sub(from).normalize();
    rod.quaternion.setFromUnitVectors(UP, this._d);
    this.root.add(rod);
    this.push({ s: rod, vel: null, ttl: 0.14, life: 0.14, grow: 1, from: 1, gravity: 0, len });
    const head = spriteOf(glowTexture(), tint, 0.38);
    head.position.copy(from); this.root.add(head);
    this.push({ s: head, vel: this._d.clone().multiplyScalar(len / 0.12), ttl: 0.12, life: 0.12, grow: 0.3, from: 0.38, gravity: 0 });
  }

  /** Projectile impact: core flash, thrown sparks, a smoke puff (scale ≈ metres). */
  impact(p: THREE.Vector3, scale: number, tint: number): void {
    this.stats.impacts++;
    const flash = spriteOf(glowTexture(), 0xffffff, scale * 1.6);
    flash.position.copy(p); this.root.add(flash);
    this.push({ s: flash, vel: null, ttl: 0.09, life: 0.09, grow: scale * 2.2, from: scale * 1.6, gravity: 0 });
    const n = Math.min(10, 4 + Math.round(scale * 3));
    for (let i = 0; i < n; i++) {
      const sp = spriteOf(glowTexture(), tint, scale * 0.3);
      sp.position.copy(p); this.root.add(sp);
      const v = new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.8, Math.random() - 0.5).normalize().multiplyScalar(scale * (4 + Math.random() * 6));
      this.push({ s: sp, vel: v, ttl: 0.35 + Math.random() * 0.25, life: 0.6, grow: scale * 0.05, from: scale * 0.3, gravity: 9 });
    }
    const smoke = spriteOf(glowTexture(), 0x3a3632, scale * 0.9, false, 0.5);
    smoke.position.copy(p); this.root.add(smoke);
    this.push({ s: smoke, vel: new THREE.Vector3(0, 0.8, 0), ttl: 0.9, life: 0.9, grow: scale * 2.6, from: scale * 0.9, gravity: 0 });
  }

  /** Heavy hit: STAGED (see the header) — flash, fireball, planar shockwave + dust, debris, lingering smoke, scorch, after-fire. */
  explosion(p: THREE.Vector3, scale: number, tint = 0xffb070): void {
    this.stats.explosions++;
    const fs = fireSystem();
    if (fs) { this.stagedExplosion(fs, p, scale, tint); return; }
    this.impact(p, scale * 1.6, tint);
    const r = spriteOf(ringTexture(), 0xfff1d0, scale * 0.8);
    r.position.copy(p); this.root.add(r);
    this.push({ s: r, vel: null, ttl: 0.45, life: 0.45, grow: scale * 7, from: scale * 0.8, gravity: 0 });
    for (let i = 0; i < 3; i++) {
      const sm = spriteOf(glowTexture(), 0x2c2826, scale * 0.8, false, 0.55);
      sm.position.copy(p).add(new THREE.Vector3((Math.random() - 0.5) * scale, 0.2, (Math.random() - 0.5) * scale));
      this.root.add(sm);
      this.push({ s: sm, vel: new THREE.Vector3((Math.random() - 0.5) * 0.6, 1.2 + Math.random(), (Math.random() - 0.5) * 0.6), ttl: 1.6 + Math.random() * 0.8, life: 2.2, grow: scale * 3.5, from: scale * 0.8, gravity: 0 });
    }
    const fire = spriteOf(glowTexture(), tint, scale * 1.2);
    fire.position.copy(p); this.root.add(fire);
    this.push({ s: fire, vel: new THREE.Vector3(0, 1.5, 0), ttl: 0.4, life: 0.4, grow: scale * 2.4, from: scale * 1.2, gravity: 0 });
  }

  private stagedExplosion(fs: NonNullable<ReturnType<typeof fireSystem>>, p: THREE.Vector3, scale: number, tint: number): void {
    const s = Math.max(0.4, scale);
    const gy = groundAt(p.x, p.z, p.y - 0.4);
    const viewer = { x: atlas.localX, y: atlas.localY, z: atlas.localZ };
    // 1. The FLASH: a broker light — the walls, the ground and the faces around it flash (bloom does not illuminate).
    // surfaces (2026-09-27): §4.3 ON THE CURVE — 50 kcd/m² over the fireball's first-80-ms disc (candela × luxToScene, cut
    // by the stops its look is held under); `intensity` stays the pre-curve A/B value.
    fs.flash({ x: p.x, y: p.y + 0.8 * s, z: p.z, color: 0xffc890, intensity: 5000 * s * s, rangeM: 25 * Math.sqrt(s), maxRangeM: 45, ms: BLAST_FLASH_MS,
      onCurve: { candela: blastFlashCandela(s), nits: EMITTER_NITS.blast, displayMax: EMITTER_DISPLAY_MAX.blast } }, viewer);
    // The hot core and the thrown sparks (the impact anatomy) — the fireball carries the body now.
    this.impact(p, s * 1.1, tint);
    // 2. The FIREBALL: expands, cools, becomes its own smoke (the fire shader's fireball).
    fs.fireball(p.x, p.y + 0.35 * s, p.z, s, 0.9 + 0.35 * Math.min(2, s));
    // 3. The SHOCKWAVE on the GROUND: a planar ring racing out + a dust skirt thrown outward.
    this.wave('ring', p.x, gy + 0.05, p.z, 0.6 * s, 9 * s, 0.1, 0.55 + 0.1 * s);
    this.wave('skirt', p.x, gy, p.z, 0.8 * s, 6.5 * s, 1.5 * s, 2.2 + 0.4 * s);
    // …and the hot air over it (high+, where the fire haze may run): a refracting disc that spreads and fades.
    if (fs.hazeOn) this.wave('haze', p.x, gy + 0.6 * s, p.z, 1.2 * s, 7 * s, 0, 0.7);
    // 4. DEBRIS: 6–14 chunks, hot and cooling, trailing embers.
    if (!this.debris) { this.debris = createDebrisPool(); this.root.add(this.debris.mesh); }
    throwDebris(this.debris, p, 6 + Math.round(Math.min(8, s * 3)), s);
    // 5. SMOKE that lingers 10–20 s (the six-way column on WebGPU med+, smoke cards elsewhere).
    fs.add(`blast-smoke:${++blastSeq}`, { x: p.x, y: gy + 0.4 * s, z: p.z, kind: 'afterfire', flame: false, size: 1.1 * s, lifeS: 10 + 10 * Math.random(), groundY: gy });
    // 6. The SCORCH.
    fs.scorch(p.x, gy, p.z, s);
    // 7. An AFTER-FIRE where a wreck-sized blast went off (a crash, a cruiser): burns 20–60 s.
    if (s >= 2.5) fs.add(`afterfire:${blastSeq}`, { x: p.x, y: gy + 0.1, z: p.z, kind: 'afterfire', size: Math.min(2, 0.5 + 0.2 * s), lifeS: 20 + 40 * Math.random(), groundY: gy });
  }

  private wave(kind: 'ring' | 'skirt' | 'haze', x: number, y: number, z: number, r0: number, r1: number, h: number, life: number): void {
    let m = this.wavePool.find((w) => w.userData.waveKind === kind);
    if (m) this.wavePool.splice(this.wavePool.indexOf(m), 1);
    else {
      m = kind === 'ring' ? new THREE.Mesh(shockRingGeometry(), shockRingMaterial())
        : kind === 'skirt' ? new THREE.Mesh(dustSkirtGeometry(), dustSkirtMaterial())
        : new THREE.Mesh(shockRingGeometry(), blastHazeMaterial().material);
      m.userData.waveKind = kind;
      m.name = kind === 'ring' ? 'explosion-ring' : kind === 'skirt' ? 'explosion-dust' : 'explosion-haze';
      m.frustumCulled = false;
      m.renderOrder = kind === 'ring' ? 7 : kind === 'skirt' ? 12 : 16;
    }
    const u = new THREE.Vector4(0, Math.random() * 9, 1, 0.3 + Math.random() * 0.4);
    m.userData[kind] = u;
    m.position.set(x, y, z);
    m.visible = true;
    this.root.add(m);
    this.waves.push({ m, kind, age: 0, life, r0, r1, h, u });
  }

  /** A medic's touch: a soft green bloom rising off the patient. */
  heal(p: THREE.Vector3): void {
    this.stats.heals++;
    for (let i = 0; i < 4; i++) {
      const sp = spriteOf(glowTexture(), 0x8cf7a0, 0.28);
      sp.position.copy(p).add(new THREE.Vector3((Math.random() - 0.5) * 0.6, Math.random() * 0.4, (Math.random() - 0.5) * 0.6));
      this.root.add(sp);
      this.push({ s: sp, vel: new THREE.Vector3(0, 0.9 + Math.random() * 0.5, 0), ttl: 0.7, life: 0.7, grow: 0.05, from: 0.28, gravity: 0 });
    }
  }

  /** Melee: a sharp spark at the point of the blow. */
  clash(p: THREE.Vector3, tint = 0xfff3c0): void {
    this.stats.impacts++;
    for (let i = 0; i < 5; i++) {
      const sp = spriteOf(glowTexture(), tint, 0.22);
      sp.position.copy(p); this.root.add(sp);
      const v = new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.6, Math.random() - 0.5).normalize().multiplyScalar(2.5 + Math.random() * 2);
      this.push({ s: sp, vel: v, ttl: 0.25 + Math.random() * 0.15, life: 0.4, grow: 0.02, from: 0.22, gravity: 9 });
    }
  }

  update(dt: number): void {
    // The staged explosion's pieces: the rings race out (fast early, slowing — a blast wave decelerates), the skirts billow.
    for (let i = this.waves.length - 1; i >= 0; i--) {
      const w = this.waves[i];
      w.age += dt;
      const k = Math.min(1, w.age / w.life);
      if (k >= 1) { w.m.visible = false; this.root.remove(w.m); this.wavePool.push(w.m); this.waves.splice(i, 1); continue; }
      const grow = 1 - (1 - k) ** 2.4;
      const r = w.r0 + (w.r1 - w.r0) * grow;
      w.u.x = k;
      if (w.kind === 'ring' || w.kind === 'haze') w.m.scale.set(2 * r, 1, 2 * r);
      else w.m.scale.set(r, w.h * (0.6 + 0.8 * grow), r);
      if (w.kind === 'haze') blastHazeMaterial().uniforms.strength.value = 0.014 * (1 - k);
    }
    if (this.debris) {
      const fs = fireSystem();
      stepDebris(this.debris, dt, (x, z) => groundAt(x, z, -1e9), (x, y, z) => fs?.sparks(x, y, z, 1));
    }
    for (let i = this.live.length - 1; i >= 0; i--) {
      const f = this.live[i];
      f.ttl -= dt;
      if (f.ttl <= 0) { disposeAny(f.s); this.live.splice(i, 1); continue; }
      const k = 1 - f.ttl / f.life;
      if (f.vel) {
        if (f.gravity) f.vel.y -= f.gravity * dt;
        f.s.position.addScaledVector(f.vel, dt);
      }
      // Shared materials: every fade is a SCALE curve (grow, then collapse at the end of life).
      if ((f.s as THREE.Sprite).isSprite) {
        const fade = k < 0.7 ? 1 : Math.max(0.01, 1 - (k - 0.7) / 0.3);
        f.s.scale.setScalar((f.from + (f.grow - f.from) * k) * fade);
      } else f.s.scale.set(0.025 * (1 - k), f.len ?? 1, 0.025 * (1 - k));
    }
  }

  get count(): number { return this.live.length; }

  dispose(): void {
    for (const f of this.live) disposeAny(f.s);
    this.live = [];
    for (const w of this.waves) this.root.remove(w.m);
    this.waves = [];
    this.root.parent?.remove(this.root);
  }
}

function disposeAny(o: THREE.Sprite | THREE.Mesh): void {
  if ((o as THREE.Sprite).isSprite) { disposeSprite(o as THREE.Sprite); return; }
  o.parent?.remove(o);
  const m = o as THREE.Mesh;
  if (m.geometry !== ROD_GEO) disposeLater(m.geometry);
  if (!isShared(m.material as THREE.Material)) disposeLater(m.material as THREE.Material);
}
